import { db } from './firebase';
import { collection, doc, getDocs, query, where, writeBatch, serverTimestamp } from 'firebase/firestore';
import type { Area, Locality, Outlet, Route, Warehouse, City } from '../types';

export type AreaRouteMixItem = {
  routeId: string | undefined;
  routeName: string;
  outletCount: number;
};

export type AreaRoutingSummaryRow = {
  area: Area;
  warehouse?: Warehouse;
  city?: City;
  localitiesCount: number;
  outletsCount: number;
  routeMix: AreaRouteMixItem[]; // sorted desc by outletCount
};

export type DryRunResult = {
  areaId: string;
  targetRouteId: string;
  targetRouteName: string;
  localitiesConsidered: number;
  localitiesToUpdate: string[]; // locality IDs
  beforeRouteMix: AreaRouteMixItem[];
  afterRouteMix: AreaRouteMixItem[];
};

// Compute an area routing summary using already-loaded datasets to avoid duplicate reads
export function buildAreaRoutingSummary(
  areas: Area[],
  localities: Locality[],
  outlets: Outlet[],
  routes: Route[],
  warehouses: Warehouse[],
  cities: City[]
): AreaRoutingSummaryRow[] {
  // Enforce custom routeId for name lookup; routes without a routeId are ignored intentionally
  const routeNameById = new Map<string, string>(
    routes
      .filter(r => !!(r as any).routeId)
      .map(r => [r.routeId as unknown as string, r.name])
  );
  const warehouseByCustomId = new Map<string, Warehouse>(warehouses.map(w => [w.warehouseId, w]));
  const cityByCode = new Map<string, City>(cities.map(c => [c.cityCode, c]));

  // Pre-group localities and outlets for performance
  const localitiesByArea = new Map<string, Locality[]>();
  for (const loc of localities.filter(l => l.isActive)) {
    const arr = localitiesByArea.get(loc.areaId) || [];
    arr.push(loc);
    localitiesByArea.set(loc.areaId, arr);
  }

  const outletsByLocality = new Map<string, Outlet[]>();
  for (const o of outlets.filter(o => o.status === 'active')) {
    const arr = outletsByLocality.get(o.localityId) || [];
    arr.push(o);
    outletsByLocality.set(o.localityId, arr);
  }

  return areas
    .filter(a => a.isActive)
    .map(area => {
      const warehouse = warehouseByCustomId.get(area.warehouseId);
      const city = warehouse ? cityByCode.get(warehouse.cityCode) : undefined;

      const areaLocalities = localitiesByArea.get(area.id) || [];
      let outletsCount = 0;
      const routeCounts = new Map<string | undefined, number>();

      for (const loc of areaLocalities) {
        const locOutlets = outletsByLocality.get(loc.id) || [];
        outletsCount += locOutlets.length;
        const rId = loc.routeId; // may be undefined
        routeCounts.set(rId, (routeCounts.get(rId) || 0) + locOutlets.length);
      }

      const routeMix: AreaRouteMixItem[] = Array.from(routeCounts.entries())
        .map(([routeId, count]) => ({
          routeId,
          routeName: routeId ? (routeNameById.get(routeId) || 'Unknown Route') : 'No Route',
          outletCount: count,
        }))
        .sort((a, b) => b.outletCount - a.outletCount);

      return {
        area,
        warehouse,
        city,
        localitiesCount: areaLocalities.length,
        outletsCount,
        routeMix,
      };
    });
}

// Dry run without writes; computes which active localities in the area would change
export function dryRunAreaReassignment(
  area: Area,
  targetRoute: Route,
  localities: Locality[],
  outlets: Outlet[],
  routes: Route[]
): DryRunResult {
  const isSameWarehouse = targetRoute.warehouseId === area.warehouseId;
  if (!isSameWarehouse) {
    throw new Error('Target route is not in the same warehouse as the area');
  }
  if (!area.isActive) {
    throw new Error('Area is inactive');
  }
  if (!targetRoute.isActive) {
    throw new Error('Target route is inactive');
  }
  if (!targetRoute.routeId) {
    throw new Error('Target route is missing required routeId');
  }

  const activeLocalities = localities.filter(l => l.isActive && l.areaId === area.id);

  // Build before route mix using outlet counts per locality.routeId
  const routeNameById = new Map<string, string>(
    routes
      .filter(r => !!(r as any).routeId)
      .map(r => [r.routeId as unknown as string, r.name])
  );
  const outletsByLocality = new Map<string, Outlet[]>();
  for (const o of outlets.filter(o => o.status === 'active')) {
    const arr = outletsByLocality.get(o.localityId) || [];
    arr.push(o);
    outletsByLocality.set(o.localityId, arr);
  }

  const beforeCounts = new Map<string | undefined, number>();
  for (const loc of activeLocalities) {
    const locOutlets = outletsByLocality.get(loc.id) || [];
    beforeCounts.set(loc.routeId, (beforeCounts.get(loc.routeId) || 0) + locOutlets.length);
  }
  const beforeRouteMix: AreaRouteMixItem[] = Array.from(beforeCounts.entries()).map(([routeId, cnt]) => ({
    routeId,
    routeName: routeId ? (routeNameById.get(routeId) || 'Unknown Route') : 'No Route',
    outletCount: cnt,
  })).sort((a, b) => b.outletCount - a.outletCount);

  const localitiesToUpdate = activeLocalities
    .filter(l => l.routeId !== targetRoute.routeId)
    .map(l => l.id);

  // After mix: all outlets from considered localities move to the target route bucket
  let totalOutlets = 0;
  for (const loc of activeLocalities) {
    totalOutlets += (outletsByLocality.get(loc.id) || []).length;
  }
  const afterRouteMix: AreaRouteMixItem[] = [
    { routeId: targetRoute.routeId, routeName: targetRoute.name, outletCount: totalOutlets }
  ];

  return {
    areaId: area.id,
    targetRouteId: targetRoute.routeId,
    targetRouteName: targetRoute.name,
    localitiesConsidered: activeLocalities.length,
    localitiesToUpdate,
    beforeRouteMix,
    afterRouteMix,
  };
}

// Commit updates to Firestore: update only active localities under the area that differ from target route
export async function reassignAreaToRoute(
  areaId: string,
  targetRoute: { routeId: string; name: string },
  actorUserId: string
): Promise<{ localitiesUpdated: number; batches: number; }> {
  // Fetch localities under area (active only)
  const locQ = query(collection(db, 'localities'), where('areaId', '==', areaId));
  const snap = await getDocs(locQ);
  const localities: { id: string; isActive?: boolean; routeId?: string }[] = [];
  snap.forEach(d => localities.push({ id: d.id, ...(d.data() as any) }));

  const toChange = localities.filter(l => l.isActive && l.routeId !== targetRoute.routeId);
  if (toChange.length === 0) return { localitiesUpdated: 0, batches: 0 };

  let idx = 0;
  let batches = 0;
  while (idx < toChange.length) {
    const batch = writeBatch(db);
    const slice = toChange.slice(idx, idx + 450); // stay well under 500
    for (const l of slice) {
      const ref = doc(db, 'localities', l.id);
      batch.update(ref, {
        routeId: targetRoute.routeId,
        routeName: targetRoute.name,
        updatedAt: serverTimestamp(),
        updatedBy: actorUserId,
      } as any);
    }
    await batch.commit();
    batches += 1;
    idx += slice.length;
  }

  // Also cascade to all outlets within these localities:
  // - set routeId/routeName
  // - clear outletSequence (null) as locality moved between routes
  const localityIds = toChange.map(l => l.id);
  // Process outlets in manageable pages per locality to avoid client OOM; Firestore lacks IN with >10 elements so iterate
  for (const lid of localityIds) {
    const q = query(collection(db, 'outlets'), where('localityId', '==', lid));
    const s = await getDocs(q);
    const outlets: { id: string }[] = [] as any;
    s.forEach(d => outlets.push({ id: d.id } as any));
    // batch updates in chunks
    let i = 0;
    while (i < outlets.length) {
      const b = writeBatch(db);
      const chunk = outlets.slice(i, i + 400);
      for (const o of chunk) {
        const ref = doc(db, 'outlets', o.id);
        b.update(ref, {
          routeId: targetRoute.routeId,
          routeName: targetRoute.name,
          outletSequence: null,
          updatedAt: serverTimestamp(),
          updatedBy: actorUserId,
        } as any);
      }
      await b.commit();
      i += chunk.length;
    }
  }

  // Write audit log
  const auditRef = doc(collection(db, 'areaRouteReassignments'));
  const auditBatch = writeBatch(db);
  auditBatch.set(auditRef, {
    areaId,
    targetRouteId: targetRoute.routeId,
    targetRouteName: targetRoute.name,
    localitiesUpdated: toChange.length,
    batches,
    createdAt: serverTimestamp(),
    createdBy: actorUserId,
    status: 'success',
  } as any);
  await auditBatch.commit();

  return { localitiesUpdated: toChange.length, batches };
}
